if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        // Add click listeners to thumbnail buttons
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        
        // Add click listeners to main viewer arrow buttons
        const viewerPrevButton = this.elements.viewer.querySelector('button[name="previous"]');
        const viewerNextButton = this.elements.viewer.querySelector('button[name="next"]');
        
        if (viewerPrevButton) {
          viewerPrevButton.addEventListener('click', () => {
            setTimeout(() => this.syncActiveMediaFromScroll(), 100);
          });
        }
        
        if (viewerNextButton) {
          viewerNextButton.addEventListener('click', () => {
            setTimeout(() => this.syncActiveMediaFromScroll(), 100);
          });
        }
        
        // Initialize drag functionality for thumbnails on desktop
        if (this.mql.matches) {
          this.initThumbnailDrag();
        }
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initThumbnailDrag() {
        const thumbnailSlider = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        if (!thumbnailSlider) return;

        let isDown = false;
        let startX;
        let scrollLeft;
        let velocity = 0;
        let lastX = 0;
        let lastTime = Date.now();

        thumbnailSlider.style.cursor = 'grab';
        thumbnailSlider.style.userSelect = 'none';

        const handleMouseDown = (e) => {
          isDown = true;
          thumbnailSlider.style.cursor = 'grabbing';
          startX = e.pageX - thumbnailSlider.offsetLeft;
          scrollLeft = thumbnailSlider.scrollLeft;
          lastX = e.pageX;
          lastTime = Date.now();
          velocity = 0;
          thumbnailSlider.style.scrollBehavior = 'auto';
        };

        const handleMouseLeave = () => {
          if (!isDown) return;
          isDown = false;
          thumbnailSlider.style.cursor = 'grab';
          applyMomentum();
        };

        const handleMouseUp = () => {
          if (!isDown) return;
          isDown = false;
          thumbnailSlider.style.cursor = 'grab';
          applyMomentum();
        };

        const handleMouseMove = (e) => {
          if (!isDown) return;
          e.preventDefault();
          
          const currentTime = Date.now();
          const currentX = e.pageX;
          const timeDelta = currentTime - lastTime;
          
          if (timeDelta > 0) {
            velocity = (currentX - lastX) / timeDelta;
          }
          
          lastX = currentX;
          lastTime = currentTime;
          
          const x = e.pageX - thumbnailSlider.offsetLeft;
          const walk = (x - startX) * 1.5;
          thumbnailSlider.scrollLeft = scrollLeft - walk;
        };

        const applyMomentum = () => {
          const friction = 0.95;
          const minVelocity = 0.5;
          
          const momentum = () => {
            if (Math.abs(velocity) > minVelocity) {
              thumbnailSlider.scrollLeft -= velocity * 10;
              velocity *= friction;
              requestAnimationFrame(momentum);
            } else {
              thumbnailSlider.style.scrollBehavior = 'smooth';
            }
          };
          
          if (Math.abs(velocity) > minVelocity) {
            requestAnimationFrame(momentum);
          } else {
            thumbnailSlider.style.scrollBehavior = 'smooth';
          }
        };

        thumbnailSlider.addEventListener('mousedown', handleMouseDown);
        thumbnailSlider.addEventListener('mouseleave', handleMouseLeave);
        thumbnailSlider.addEventListener('mouseup', handleMouseUp);
        thumbnailSlider.addEventListener('mousemove', handleMouseMove);

        // Prevent click events when dragging
        thumbnailSlider.addEventListener('click', (e) => {
          if (Math.abs(velocity) > 1) {
            e.preventDefault();
            e.stopPropagation();
          }
        }, true);
      }

      syncActiveMediaFromScroll() {
        const viewerSlider = this.elements.viewer.slider;
        if (!viewerSlider) return;
        
        // Find the currently visible slide
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        let activeSlide = null;
        
        slides.forEach((slide) => {
          const slideRect = slide.getBoundingClientRect();
          const sliderRect = viewerSlider.getBoundingClientRect();
          
          // Check if slide is in view
          if (slideRect.left >= sliderRect.left - 10 && slideRect.left <= sliderRect.left + 10) {
            activeSlide = slide;
          }
        });
        
        if (activeSlide) {
          const mediaId = activeSlide.dataset.mediaId;
          
          // Update active class
          slides.forEach((slide) => slide.classList.remove('is-active'));
          activeSlide.classList.add('is-active');
          
          // Update thumbnail
          if (this.elements.thumbnails) {
            const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            this.setActiveThumbnail(thumbnail);
            if (thumbnail) {
              this.announceLiveRegion(activeSlide, thumbnail.dataset.mediaPosition);
            }
          }
          
          // Play media
          this.playActiveMedia(activeSlide);
        }
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;
        
        const mediaId = currentElement.dataset.mediaId;
        if (!mediaId) return;
        
        // Update active media
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        currentElement.classList.add('is-active');
        
        // Update thumbnail
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
          if (thumbnail) {
            this.announceLiveRegion(currentElement, thumbnail.dataset.mediaPosition);
          }
        }
        
        // Play media if needed
        this.playActiveMedia(currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
